/**
 * Hand-rolled validator for {@link LevelData} JSON. Returns the parsed value
 * on success or up to 10 human-readable errors with JSON-pointer paths.
 *
 * Kept dependency-free so the gameplay bundle pays nothing for this file even
 * when the editor is dynamically imported.
 */

import type { LevelData } from '../gameplay/LevelLoader.js';

export type ValidateResult =
  | { ok: true; data: LevelData }
  | { ok: false; errors: string[] };

const MAX_ERRORS = 10;

export function validate(input: unknown): ValidateResult {
  const errs: string[] = [];
  const push = (path: string, msg: string) => {
    if (errs.length < MAX_ERRORS) errs.push(`${path || '/'} ${msg}`);
  };

  if (!isObj(input)) {
    return { ok: false, errors: ['/ must be an object'] };
  }

  const v = input as Record<string, unknown>;

  reqString(v, 'id', '/id', push);
  reqString(v, 'name', '/name', push);
  reqVec(v.spawn, '/spawn', push);
  optVec(v.spawnVelocity, '/spawnVelocity', push);
  reqRect(v.finish, '/finish', push);
  reqWorldBounds(v.worldBounds, '/worldBounds', push);
  reqArray(v.anchors, '/anchors', push, (item, p) => validateAnchor(item, p, push));
  reqArray(v.platforms, '/platforms', push, (item, p) => validatePlatform(item, p, push));
  reqArray(v.hazards, '/hazards', push, (item, p) => validateRect(item, p, push));
  optArray(v.trampolines, '/trampolines', push, (item, p) => validateTrampoline(item, p, push));
  optArray(v.boosters, '/boosters', push, (item, p) => validateBooster(item, p, push));
  optArray(v.wind, '/wind', push, (item, p) => validateWind(item, p, push));
  optArray(v.movingPlatforms, '/movingPlatforms', push, (item, p) => validateMovingPlatform(item, p, push));
  optArray(v.movingHazards, '/movingHazards', push, (item, p) => validateMovingHazard(item, p, push));
  optArray(v.switches, '/switches', push, (item, p) => validateSwitch(item, p, push));
  optArray(v.gates, '/gates', push, (item, p) => validateGate(item, p, push));
  optArray(v.checkpoints, '/checkpoints', push, (item, p) => validateCheckpoint(item, p, push));
  if (v.bgGradient !== undefined) validateBgGradient(v.bgGradient, '/bgGradient', push);

  if (errs.length > 0) return { ok: false, errors: errs };
  return { ok: true, data: input as unknown as LevelData };
}

// --- Field helpers ---------------------------------------------------------

function isObj(x: unknown): x is Record<string, unknown> {
  return x !== null && typeof x === 'object' && !Array.isArray(x);
}

function isFiniteNum(x: unknown): x is number {
  return typeof x === 'number' && Number.isFinite(x);
}

function reqString(o: Record<string, unknown>, k: string, p: string, push: (p: string, m: string) => void): void {
  const v = o[k];
  if (typeof v !== 'string' || v.length === 0) push(p, 'must be a non-empty string');
}

function reqVec(v: unknown, p: string, push: (p: string, m: string) => void): void {
  if (!isObj(v)) { push(p, 'must be { x, y }'); return; }
  if (!isFiniteNum(v.x)) push(`${p}/x`, 'must be a finite number');
  if (!isFiniteNum(v.y)) push(`${p}/y`, 'must be a finite number');
}

function optVec(v: unknown, p: string, push: (p: string, m: string) => void): void {
  if (v === undefined) return;
  reqVec(v, p, push);
}

function reqRect(v: unknown, p: string, push: (p: string, m: string) => void): void {
  if (!isObj(v)) { push(p, 'must be { x, y, w, h }'); return; }
  if (!isFiniteNum(v.x)) push(`${p}/x`, 'must be a finite number');
  if (!isFiniteNum(v.y)) push(`${p}/y`, 'must be a finite number');
  if (!isFiniteNum(v.w) || (v.w as number) <= 0) push(`${p}/w`, 'must be > 0');
  if (!isFiniteNum(v.h) || (v.h as number) <= 0) push(`${p}/h`, 'must be > 0');
}

function reqWorldBounds(v: unknown, p: string, push: (p: string, m: string) => void): void {
  if (!isObj(v)) { push(p, 'must be { minX, maxX, maxY }'); return; }
  if (!isFiniteNum(v.minX)) push(`${p}/minX`, 'must be a finite number');
  if (!isFiniteNum(v.maxX)) push(`${p}/maxX`, 'must be a finite number');
  if (!isFiniteNum(v.maxY) || (v.maxY as number) <= 0) push(`${p}/maxY`, 'must be > 0');
  if (isFiniteNum(v.minX) && isFiniteNum(v.maxX) && (v.maxX as number) <= (v.minX as number)) {
    push(`${p}/maxX`, 'must be greater than minX');
  }
}

function reqArray(
  v: unknown,
  p: string,
  push: (p: string, m: string) => void,
  each: (item: unknown, path: string) => void,
): void {
  if (!Array.isArray(v)) { push(p, 'must be an array'); return; }
  for (let i = 0; i < v.length; i++) each(v[i], `${p}/${i}`);
}

function optArray(
  v: unknown,
  p: string,
  push: (p: string, m: string) => void,
  each: (item: unknown, path: string) => void,
): void {
  if (v === undefined) return;
  reqArray(v, p, push, each);
}

function validateAnchor(item: unknown, p: string, push: (p: string, m: string) => void): void {
  if (!isObj(item)) { push(p, 'must be an object'); return; }
  if (!isFiniteNum(item.x)) push(`${p}/x`, 'must be a finite number');
  if (!isFiniteNum(item.y)) push(`${p}/y`, 'must be a finite number');
  if (!isFiniteNum(item.hookRadius) || (item.hookRadius as number) <= 0) {
    push(`${p}/hookRadius`, 'must be > 0');
  }
  if (item.path !== undefined) validatePath(item.path, `${p}/path`, push);
}

function validatePath(item: unknown, p: string, push: (p: string, m: string) => void): void {
  if (!isObj(item)) { push(p, 'must be { points, speed, loop }'); return; }
  if (!Array.isArray(item.points) || item.points.length < 2) {
    push(`${p}/points`, 'must be an array of >= 2 waypoints');
  } else {
    for (let i = 0; i < item.points.length; i++) reqVec(item.points[i], `${p}/points/${i}`, push);
  }
  if (!isFiniteNum(item.speed) || (item.speed as number) <= 0) push(`${p}/speed`, 'must be > 0');
  if (item.loop !== 'cycle' && item.loop !== 'pingpong') push(`${p}/loop`, "must be 'cycle' or 'pingpong'");
  if (item.phase !== undefined && !isFiniteNum(item.phase)) push(`${p}/phase`, 'must be a finite number');
}

function validateMovingPlatform(item: unknown, p: string, push: (p: string, m: string) => void): void {
  reqRect(item, p, push);
  if (!isObj(item)) return;
  if (item.angle !== undefined && !isFiniteNum(item.angle)) push(`${p}/angle`, 'must be a finite number');
  if (item.bouncy !== undefined && typeof item.bouncy !== 'boolean') push(`${p}/bouncy`, 'must be a boolean');
  if (item.path === undefined) push(`${p}/path`, 'is required');
  else validatePath(item.path, `${p}/path`, push);
}

function validateMovingHazard(item: unknown, p: string, push: (p: string, m: string) => void): void {
  reqRect(item, p, push);
  if (!isObj(item)) return;
  if (item.path === undefined) push(`${p}/path`, 'is required');
  else validatePath(item.path, `${p}/path`, push);
}

function validateSwitch(item: unknown, p: string, push: (p: string, m: string) => void): void {
  reqRect(item, p, push);
  if (!isObj(item)) return;
  if (typeof item.id !== 'string' || item.id.length === 0) push(`${p}/id`, 'must be a non-empty string');
  if (item.mode !== undefined && item.mode !== 'toggle' && item.mode !== 'momentary') {
    push(`${p}/mode`, "must be 'toggle' or 'momentary'");
  }
}

function validateGate(item: unknown, p: string, push: (p: string, m: string) => void): void {
  reqRect(item, p, push);
  if (!isObj(item)) return;
  if (typeof item.id !== 'string' || item.id.length === 0) push(`${p}/id`, 'must be a non-empty string');
  if (item.angle !== undefined && !isFiniteNum(item.angle)) push(`${p}/angle`, 'must be a finite number');
  if (item.inverted !== undefined && typeof item.inverted !== 'boolean') push(`${p}/inverted`, 'must be a boolean');
}

function validateCheckpoint(item: unknown, p: string, push: (p: string, m: string) => void): void {
  if (!isObj(item)) { push(p, 'must be an object'); return; }
  if (!isFiniteNum(item.x)) push(`${p}/x`, 'must be a finite number');
  if (!isFiniteNum(item.y)) push(`${p}/y`, 'must be a finite number');
  if (item.vx !== undefined && !isFiniteNum(item.vx)) push(`${p}/vx`, 'must be a finite number');
  if (item.vy !== undefined && !isFiniteNum(item.vy)) push(`${p}/vy`, 'must be a finite number');
}

function validateRect(item: unknown, p: string, push: (p: string, m: string) => void): void {
  reqRect(item, p, push);
}

function validatePlatform(item: unknown, p: string, push: (p: string, m: string) => void): void {
  reqRect(item, p, push);
  if (!isObj(item)) return;
  if (item.angle !== undefined && !isFiniteNum(item.angle)) push(`${p}/angle`, 'must be a finite number');
  if (item.bouncy !== undefined && typeof item.bouncy !== 'boolean') push(`${p}/bouncy`, 'must be a boolean');
}

function validateTrampoline(item: unknown, p: string, push: (p: string, m: string) => void): void {
  reqRect(item, p, push);
  if (!isObj(item)) return;
  if (item.power !== undefined && (!isFiniteNum(item.power) || (item.power as number) <= 0)) {
    push(`${p}/power`, 'must be > 0');
  }
  if (item.direction !== undefined &&
      item.direction !== 'up' && item.direction !== 'down' &&
      item.direction !== 'left' && item.direction !== 'right') {
    push(`${p}/direction`, 'must be one of up|down|left|right');
  }
}

function validateBooster(item: unknown, p: string, push: (p: string, m: string) => void): void {
  reqRect(item, p, push);
  if (!isObj(item)) return;
  if (item.vx !== undefined && !isFiniteNum(item.vx)) push(`${p}/vx`, 'must be a finite number');
  if (item.vy !== undefined && !isFiniteNum(item.vy)) push(`${p}/vy`, 'must be a finite number');
}

function validateWind(item: unknown, p: string, push: (p: string, m: string) => void): void {
  reqRect(item, p, push);
  if (!isObj(item)) return;
  if (item.fx !== undefined && !isFiniteNum(item.fx)) push(`${p}/fx`, 'must be a finite number');
  if (item.fy !== undefined && !isFiniteNum(item.fy)) push(`${p}/fy`, 'must be a finite number');
}

function validateBgGradient(v: unknown, p: string, push: (p: string, m: string) => void): void {
  if (!isObj(v)) { push(p, 'must be { top, bottom }'); return; }
  if (typeof v.top !== 'string') push(`${p}/top`, 'must be a hex color string');
  if (typeof v.bottom !== 'string') push(`${p}/bottom`, 'must be a hex color string');
}

/** Parse + validate a JSON string in one call. */
export function parseJson(text: string): ValidateResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, errors: [`/ invalid JSON: ${msg}`] };
  }
  return validate(parsed);
}

/** Deep clone via JSON round-trip. Safe because LevelData is plain JSON. */
export function cloneLevel(data: LevelData): LevelData {
  return JSON.parse(JSON.stringify(data)) as LevelData;
}
